import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchPatientById,
  fetchPatientCases,
  modifyPatientDetails,
  fetchPatientSchemes,
  addPatientDetails,
  postSchemeToGetPatient,
  getPatientBasicDetails,
  getPreAuthQueryData,
  downloadReport,
  fetchReferralList,
  deleteUploadedDocument,
  getQuestionsDeficiencyCheck,
  getSavedDocumentDeficiencyCheck,
  postQuestionDeficiencyCheck,
  fetchPatientCasesWithVisits,
  fetchPatientDetailsUHID,
  fetchPatientDoCDeficiency,
} from "../apis/patientService";
import {
  IPatient,
  IPatientFetchResponse,
  IPatientCasesFetchResponse,
  IPatientSchemeList,
  IFetchPatientBasicDetails,
  IFetchPatientDetailsFromPortalResponse,
  IPatientFetchDetailsFromPortal,
  IPatientPreAuthQueryGetDetails,
  IDeficiencyCheck,
  IPatientCasesWithVisitsFetchResponse,
  IPatientDocDeficiencyState,
  IPatientDocDeficiencyFilter,
} from "../interfaces/IPatient";
import { readOcrDoc, uploadDocs } from "../apis/caseService";
import { IDocumentFields } from "../interfaces/ICase";
import { errorMessage } from "../utils/notificationUtils";
import { LIST_DEFAULT_PAGE_SIZE } from "../utils/constants";

const initialState: IPatientDocDeficiencyState = {
  totalAvailablePatients: 0,
  patients: {},
  pageToPatientIdMap: {},
  pageToVisitIdMap: {},
  isFetchingAllPatients: false,
  deficiencyCheckQuestions: null,
  error: null,
  patientFilters: {
    endDate: "",
    orderBy: "DESC",
    pageNumber: 1,
    pageSize: LIST_DEFAULT_PAGE_SIZE,
    searchTerm: null,
    startDate: "",
    allDocuments: true,
    deficiencyCheckStatus: null,
  },
  fetchBasicDetails: null,
  isLoading: false,
  isDownloadingReport: false,
};

export const getPatients = createAsyncThunk(
  "patients/getPatients",
  async ({
    filters,
    accessToken,
    healthCareId,
  }: {
    filters: IPatientDocDeficiencyFilter;
    accessToken: string;
    healthCareId: number;
  }) => {
    // handling 0 base indexing on server
    filters = { ...filters, pageNumber: filters.pageNumber - 1 };
    const response = await fetchPatientDoCDeficiency(filters, healthCareId);
    return response;
  }
);

export const getPatientCases = createAsyncThunk(
  "patients/getPatientCases",
  async ({ id, accessToken }: { id: number; accessToken: string }) => {
    const response = await fetchPatientCases(id);
    return response;
  }
);

// export const getPatientCasesWithVisits = createAsyncThunk(
//   "patients/getPatientCasesWithVisits",
//   async ({
//     id,
//     billType,
//     accessToken,
//   }: {
//     id: number;
//     billType: string;
//     accessToken: string;
//   }) => {
//     const response = await fetchPatientCasesWithVisits(id, billType);
//     return response;
//   }
// );

export const getPatientReferral = createAsyncThunk(
  "patients/getPatientReferral",
  async (patientId: number) => {
    const response = await fetchReferralList(patientId);
    return response;
  }
);

export const getPatientSchemes = createAsyncThunk(
  "patients/getPatientSchemes",
  async ({ id, accessToken }: { id: number; accessToken: string }) => {
    const response = await fetchPatientSchemes(id);
    return response;
  }
);

export const getPatientById = createAsyncThunk(
  "patients/getPatientById",
  async ({ id, accessToken }: { id: number; accessToken: string }) => {
    const response = await fetchPatientById(id);
    return response;
  }
);

export const downloadReportScheme = createAsyncThunk(
  "patients/downloadReportScheme",
  async (formData: any) => {
    await downloadReport(formData);
  }
);

export const getDeficiencyQuestions = createAsyncThunk(
  "patients/getDeficiencyQuestions",
  async (documentId: number) => {
    const response = await getQuestionsDeficiencyCheck(documentId);
    return response;
  }
);

export const getDeficiencyQuestionData = createAsyncThunk(
  "patients/getDeficiencyQuestionData",
  async (documentId: number) => {
    const response = await getSavedDocumentDeficiencyCheck(documentId);
    return response;
  }
);

export const readOcrDocs = createAsyncThunk(
  "patients/readOcrDocs",
  async ({
    formData,
    accessToken,
  }: {
    formData: FormData;
    accessToken: string;
  }) => {
    const response = await readOcrDoc(formData, accessToken);
    return response;
  }
);

export const fetchPatientDetailsFromPortal = createAsyncThunk(
  "patients/fetchPatientDetailsFromPortal",
  async ({
    formData,
    accessToken,
  }: {
    formData: IPatientFetchDetailsFromPortal;
    accessToken: string;
  }) => {
    const response = await postSchemeToGetPatient(formData);
    return response;
  }
);

export const UpdatePatientDetails = createAsyncThunk(
  "patients/UpdatePatientDetails",
  async ({
    id,
    formValues,
    accessToken,
  }: {
    id: number;
    formValues: object;
    accessToken: string;
  }) => {
    const response = await modifyPatientDetails(id, formValues);
    return response;
  }
);

export const getBasicDetails = createAsyncThunk(
  "patients/getBasicDetails",
  async ({ id }: { id: number }) => {
    const response = await getPatientBasicDetails(id);
    return response;
  }
);
export const postPatientDetails = createAsyncThunk(
  "patients/postPatientDetails",
  async ({
    formValues,
    accessToken,
  }: {
    formValues: object;
    accessToken: string;
  }) => {
    const response = await addPatientDetails(formValues);
    return response;
  }
);

export const getPatientDetailsUsingUHID = createAsyncThunk(
  "patients/getPatientDetailsUsingUHID",
  async ({
    formData,
    schemeMetaDataId,
  }: {
    formData: any;
    schemeMetaDataId: number;
  }) => {
    const response = await fetchPatientDetailsUHID(formData, schemeMetaDataId);
    return response;
  }
);

export const getPreAuthQueryDetails = createAsyncThunk(
  "patients/getPreAuthQueryDetails",
  async ({
    data,
    caseId,
    schemeMetaDataId,
    visitInfoId,
  }: {
    data: string;
    caseId: number | undefined;
    schemeMetaDataId: number | undefined;
    visitInfoId: number | undefined;
  }) => {
    const response = await getPreAuthQueryData(
      data,
      caseId,
      visitInfoId,
      schemeMetaDataId
    );
    return response;
  }
);

export const updateDocuments = createAsyncThunk(
  "patients/getReferralById",
  async (formValues: any) => {
    const response = await deleteUploadedDocument(formValues);
    return response;
  }
);

export const postDeficiencyQuestion = createAsyncThunk(
  "patients/postDeficiencyQuestion",
  async (formValues: any) => {
    const response = await postQuestionDeficiencyCheck(formValues);
    return response;
  }
);

export const uploadDocument = createAsyncThunk(
  "patients/uploadDocument",
  async (formValues: any) => {
    const res = await uploadDocs(formValues);
    return res;
  }
);

const documentDeficiencySlice = createSlice({
  name: "documentDeficiency",
  initialState,
  reducers: {
    // updateSelectedPatient: (state, action: PayloadAction<IPatient>) => {
    //   state.selectedPatient = action.payload;
    // },
    // resetPatientToCasesMap: (state) => {
    //   state.patientToCasesMap = {};
    // },

    setPatientFilters: (
      state,
      action: PayloadAction<IPatientDocDeficiencyFilter>
    ) => {
      state.patientFilters = action.payload;
    },
    clearPageToPatientIdMap: (state) => {
      state.pageToPatientIdMap = {};
    },
    clearPageToVisitIdMap: (state) => {
      state.pageToVisitIdMap = {};
    },
    clearFetchBasicDetail: (state, action) => {
      state.fetchBasicDetails = null;
    },
    clearDeficiencyCheck: (state) => {
      state.deficiencyCheckQuestions = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPatients.pending, (state) => {
        state.patients = {};
        state.pageToPatientIdMap = {};
        state.pageToVisitIdMap = {};
        state.isFetchingAllPatients = true;
        state.error = null;
      })
      .addCase(getPatients.fulfilled, (state, action: any) => {
        state.isFetchingAllPatients = false;

        action.payload.patientResponseList?.forEach((patient: any) => {
          if (patient?.visitId) {
            state.patients[patient.visitId] = patient;

            if (!state.pageToVisitIdMap[state.patientFilters.pageNumber]) {
              state.pageToVisitIdMap[state.patientFilters.pageNumber] = [];
            }
            if (
              !state.pageToVisitIdMap[state.patientFilters.pageNumber].includes(
                patient?.visitId
              )
            ) {
              state.pageToVisitIdMap[state.patientFilters.pageNumber].push(
                patient.visitId
              );
            }
          }

          // if (patient?.patientId) {
          //   state.patients[patient.patientId] = patient;

          //   if (!state.pageToPatientIdMap[state.patientFilters.pageNumber]) {
          //     state.pageToPatientIdMap[state.patientFilters.pageNumber] = [];
          //   }
          //   if (
          //     !state.pageToPatientIdMap[
          //       state.patientFilters.pageNumber
          //     ].includes(patient.patientId)
          //   ) {
          //     state.pageToPatientIdMap[state.patientFilters.pageNumber].push(
          //       patient.patientId
          //     );
          //   }
          // }
        });

        state.totalAvailablePatients = action.payload.totalElements || 0;
        state.patientFilters.pageSize = action.meta.arg.filters.pageSize;
        state.error = null;
      })
      .addCase(getPatients.rejected, (state, action) => {
        state.isFetchingAllPatients = false;
        state.error = action.error.message || "Failed to fetch patients";
      })
      .addCase(getDeficiencyQuestions.pending, (state) => {
        state.error = null;
      })
      .addCase(
        getDeficiencyQuestions.fulfilled,
        (state, action: PayloadAction<Array<IDeficiencyCheck>>) => {
          state.deficiencyCheckQuestions = action.payload;
        }
      )
      .addCase(getDeficiencyQuestions.rejected, (state, action) => {
        state.isDownloadingReport = false;
      })
      .addCase(getDeficiencyQuestionData.pending, (state) => {
        state.error = null;
      })
      .addCase(
        getDeficiencyQuestionData.fulfilled,
        (state, action: PayloadAction<any>) => {
          if (typeof action.payload === "object") {
            state.deficiencyCheckQuestions = action.payload;
          }
        }
      )
      .addCase(getDeficiencyQuestionData.rejected, (state, action) => {
        state.isDownloadingReport = false;
      })
      .addCase(postDeficiencyQuestion.pending, (state) => {
        state.error = null;
      })
      .addCase(
        postDeficiencyQuestion.fulfilled,
        (state, action: PayloadAction<any>) => {
          if (typeof action.payload === "object") {
            state.deficiencyCheckQuestions = action.payload;
          }
        }
      )
      .addCase(postDeficiencyQuestion.rejected, (state, action) => {
        state.isDownloadingReport = false;
      });
  },
});

export const {
  clearFetchBasicDetail,
  setPatientFilters,
  clearPageToPatientIdMap,
  clearPageToVisitIdMap,
  clearDeficiencyCheck,
} = documentDeficiencySlice.actions;
export default documentDeficiencySlice.reducer;
